import { AiStars, Check, X } from "@ignite-analytics/icons";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { answerIds } from "./answers";
import type { Question } from "./contents";
import type { Review } from "./prefill";

type Props = {
    question: Question;
    review: Review;
    onConfirm: () => void;
    onReject: () => void;
};

const useSuggestedText = (question: Question, review: Review) => {
    const { formatMessage, formatList } = useIntl();
    const ids = answerIds(review.suggestion.value as string | number | string[]);
    if (question.type === "boolean")
        return ids[0] === "yes" ? formatMessage({ defaultMessage: "Yes" }) : formatMessage({ defaultMessage: "No" });
    if (question.type === "singleSelect" || question.type === "multiSelect")
        return formatList(ids.map((id) => question.options.find((o) => o.id === id)?.label ?? id));
    return formatList(ids);
};

export const SuggestionCard: React.FC<Props> = ({ question, review, onConfirm, onReject }) => {
    const { formatMessage } = useIntl();
    const suggested = useSuggestedText(question, review);
    const { suggestion, status } = review;
    const source = suggestion.page ? (
        <FormattedMessage
            defaultMessage="Page {page} of {fileName}"
            values={{ fileName: suggestion.fileName, page: suggestion.page }}
        />
    ) : (
        <FormattedMessage defaultMessage="Found in {fileName}" values={{ fileName: suggestion.fileName }} />
    );

    if (status === "confirmed")
        return (
            <Typography
                variant="textXs"
                color="textTextHelper"
                title={suggestion.snippet}
                sx={{ display: "block", px: 1.5 }}
                noWrap
            >
                {source}
            </Typography>
        );

    if (status === "edited")
        return (
            <Typography variant="textXs" color="textTextSecondary" sx={{ display: "block", px: 1.5 }}>
                <FormattedMessage
                    defaultMessage="Changed by you. Suggested was “{suggested}”."
                    values={{ suggested }}
                />
            </Typography>
        );

    return (
        <Stack
            spacing={0.75}
            sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                boxShadow: "0 1px 2px rgba(13, 16, 23, 0.04)",
            }}
        >
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <AiStars fontSize="small" sx={{ color: (theme) => theme.palette.tokens?.icon.iconPurple }} />
                <Typography variant="textXs" fontWeight={500} sx={{ flex: 1 }}>
                    <FormattedMessage defaultMessage="Suggested" />
                </Typography>
                <Tooltip title={formatMessage({ defaultMessage: "Confirm" })}>
                    <IconButton
                        size="small"
                        color="success"
                        aria-label={formatMessage({ defaultMessage: "Confirm" })}
                        onClick={onConfirm}
                    >
                        <Check fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={formatMessage({ defaultMessage: "Reject" })}>
                    <IconButton
                        size="small"
                        aria-label={formatMessage({ defaultMessage: "Reject" })}
                        onClick={onReject}
                    >
                        <X fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography
                variant="textXs"
                color="textTextSecondary"
                title={suggestion.snippet}
                sx={{
                    fontStyle: "italic",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}
            >
                <FormattedMessage defaultMessage="“{snippet}”" values={{ snippet: suggestion.snippet }} />
            </Typography>
            <Typography variant="textXs" color="textTextHelper" noWrap>
                {source}
            </Typography>
        </Stack>
    );
};
